'use client';

import CustomChakraProvider from '~/providers/Chakra';
import { MobileProvider } from '~/providers/Mobile';
import { PLASMIC } from '~/lib/plasmic/plasmic-init';
import { PlasmicClientRootProvider } from '~/lib/plasmic/plasmic-init-client';
import { theme as proTheme } from '@chakra-ui/pro-theme';
import { extendTheme } from '@chakra-ui/react';
import { colors } from '~/theme/colors';
import { getAgencyComponents } from '~/theme/components';
import { useAgency } from '~/hooks/useAgency';
import { Component, useEffect, useState, useMemo, PropsWithChildren } from 'react';
import { PLASMIC_STUDIO_COMPONENTS } from '~/lib/plasmic/load-components';

class DebugErrorBoundary extends Component<PropsWithChildren, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[DebugErrorBoundary] Error:', error.message);
    console.error('[DebugErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const FALLBACK_FONT =
  'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export function Providers({ children }: PropsWithChildren) {
  const {
    agency,
    getBreakpoints,
    getBrandColor,
    getChakraColors,
    getFonts,
    getGlobalStyles,
  } = useAgency();
  const [plasmicData, setPlasmicData] = useState<any>(null);

  const {
    heading: headingFont,
    headingSmall: headingSmallFont,
    body: bodyFont,
  } = getFonts();

  const customTheme = useMemo(() =>
    extendTheme(proTheme, {
      styles: {
        global: getGlobalStyles(),
      },
      fonts: {
        heading: headingFont || FALLBACK_FONT,
        headingSmall: headingSmallFont || FALLBACK_FONT,
        body: bodyFont || FALLBACK_FONT,
      },
      breakpoints: getBreakpoints(),
      colors: {
        ...colors,
        ...getChakraColors(),
        brand: getBrandColor(),
      },
      config: {
        initialColorMode: 'light',
        useSystemColorMode: false,
        disableTransitionOnChange: false,
      },
      components: getAgencyComponents(agency?.identifier),
    }),
    [agency?.identifier, headingFont, headingSmallFont, bodyFont]
  );

  useEffect(() => {
    // Defer Plasmic fetch to not block initial render
    // Use requestIdleCallback if available, otherwise setTimeout
    let cancelled = false;

    const fetchPlasmicData = async () => {
      try {
        const componentsData = await PLASMIC.fetchComponentData(
          PLASMIC_STUDIO_COMPONENTS
        );
        if (!cancelled) {
          setPlasmicData(componentsData);
        }
      } catch (error) {
        // Silently fail - Plasmic components will just not render
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to fetch Plasmic data:', error);
        }
      }
    };

    if (typeof requestIdleCallback !== 'undefined') {
      const handle = requestIdleCallback(() => fetchPlasmicData());
      return () => {
        cancelled = true;
        cancelIdleCallback(handle);
      };
    } else {
      const handle = setTimeout(() => fetchPlasmicData(), 100);
      return () => {
        cancelled = true;
        clearTimeout(handle);
      };
    }
  }, []);

  return (
    <DebugErrorBoundary>
      <MobileProvider>
        <CustomChakraProvider customTheme={customTheme}>
          <PlasmicClientRootProvider prefetchedData={plasmicData}>
            {children}
          </PlasmicClientRootProvider>
        </CustomChakraProvider>
      </MobileProvider>
    </DebugErrorBoundary>
  );
}
